import { useEffect, type RefObject } from "react";
import { IS_IOS } from "@/lib/environmentDetector";
import { isKeyboardSummoningFocus } from "@/lib/keyboardFocus";

export const APP_HEIGHT_VAR = "--app-height";

/**
 * Late re-reads for WebKit's stale-viewport-metrics-inside-the-resize-handler
 * quirk. The keyboard retract animation runs ~300ms, so a single early
 * settle read can only ever see the pre-dismissal height — the tail
 * entries are what catch the real end state when WebKit fires no further
 * resize of its own.
 */
const SETTLE_DELAYS_MS = [80, 300, 600];

/**
 * Grace period before the shell expands again once focus leaves. Covers
 * the keyboard's retract animation (so the layout grows into the space
 * the keyboard is vacating instead of ahead of it) and absorbs the
 * blur→refocus flicker of tapping a button next to a composer.
 */
const RELEASE_DELAY_MS = 250;

/**
 * While the shell is held short, re-check the invariant on a timer. This
 * is the only backstop for a dismissal that fires NO event at all —
 * unmounting the focused input during a route change takes the keyboard
 * down without a reliable `focusout`, and if WebKit also swallows the
 * resize, nothing else would ever call `sync` again. Cheap: a couple of
 * property reads, and only while a keyboard is actually up.
 */
const WATCHDOG_MS = 500;

/**
 * iOS keyboard guard — standalone (PWA) and browser tabs alike (every
 * iOS browser is a WebKit shell with the same keyboard behavior).
 *
 * iOS never resizes the layout viewport for the on-screen keyboard
 * (`interactive-widget=resizes-content` is unimplemented in WebKit).
 * Instead it PANS the layout viewport up so the focused field stays
 * visible — dragging every `position:fixed` box, including the app
 * shell's safe-area padding, off screen. In standalone that leaves raw
 * content under the OS status bar; in a browser tab the shell's top
 * slides under the browser chrome and bare page background fills the
 * gap between the (shrunken) shell's bottom edge and the keyboard.
 *
 * Countermeasures, all driven by the `visualViewport` API (the same
 * recipe Telegram Web A ships in production):
 * 1. Mirror `visualViewport.height` into the `--app-height` root CSS
 *    variable so the app shell shrinks above the keyboard — the focused
 *    composer ends up above the keyboard by layout, not by panning.
 * 2. Undo any document scroll the pan created (`scrollTo(0, 0)`); safe
 *    in every mode because the shell is never user-scrollable
 *    (`html { overflow: hidden }`), so document scroll can only come
 *    from the keyboard pan — and after (1) the focused field is visible
 *    without it. Only where the `#Main` shell exists, though: routes
 *    outside ChatLayout/AuthLayout don't consume `--app-height`, so (1)
 *    gives no such guarantee there and the pan must be left alone.
 * 3. Re-anchor the app shell (`#Main`) and the status-bar scrim to the
 *    *visual* viewport top via `translateY(offsetTop)` for pans that
 *    aren't backed by document scroll and for the animation frames
 *    where the pan is still winning.
 *
 * THE INVARIANT (and why it exists): every write above is applied ONLY
 * while something that summons a keyboard holds focus, and is undone
 * otherwise. `--app-height` is a document-level variable owned by a hook
 * that mounts once and never unmounts, so a value written from a stale
 * `visualViewport.height` survives for the life of the app: the shell
 * renders at keyboard height forever, a third of the screen goes dead,
 * and only relaunching the app clears it (Robert, prod v1.1.29, 08-08).
 * Trusting WebKit to always deliver the resize that announces dismissal
 * is what made that reachable — iOS drops it when the app is suspended
 * with the keyboard up, and when the focused input is unmounted mid
 * route change. So the guard no longer needs that event to be correct:
 * no keyboard-summoning focus ⇒ release, re-checked on resume and on a
 * watchdog tick. With nothing set, the shell falls back to `100dvh`,
 * which is the same height a correct `--app-height` would have held.
 *
 * The transform in (3) is applied only while a pan offset exists — a
 * transformed ancestor becomes the containing block for `fixed`
 * descendants, so it must never linger once the viewports realign or
 * focus leaves the editable.
 */
export function useIosViewportGuard(scrimRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!IS_IOS || !vv) {
      return;
    }

    const root = document.documentElement;
    const scrimEl = scrimRef.current;
    let frame = 0;
    let settleTimers: number[] = [];
    let releaseTimer = 0;
    let watchdogTimer = 0;
    /** Are our writes currently applied? Gates the watchdog and the release. */
    let held = false;

    const clearGlue = () => {
      for (const el of [scrimEl, document.getElementById("Main")]) {
        el?.style.removeProperty("transform");
      }
    };

    const stopWatchdog = () => {
      window.clearInterval(watchdogTimer);
      watchdogTimer = 0;
    };

    const cancelRelease = () => {
      window.clearTimeout(releaseTimer);
      releaseTimer = 0;
    };

    const release = () => {
      cancelRelease();
      stopWatchdog();
      held = false;
      root.style.removeProperty(APP_HEIGHT_VAR);
      clearGlue();
    };

    /**
     * Let go a beat late: at `focusout` the keyboard is still sliding
     * out, and growing the shell to full height before the screen is
     * free would put its bottom edge behind the keyboard for the rest of
     * the animation.
     */
    const scheduleRelease = () => {
      if (!held || releaseTimer) {
        return;
      }
      releaseTimer = window.setTimeout(() => {
        releaseTimer = 0;
        if (!isKeyboardSummoningFocus()) {
          release();
        }
      }, RELEASE_DELAY_MS);
    };

    const sync = () => {
      // The settle timers call sync directly; drop any rAF still pending
      // (e.g. throttled in a backgrounded tab) so its handle is never
      // orphaned — an untracked frame would survive cleanup and re-apply
      // styles after unmount. No-op when sync IS the running rAF callback.
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      // Pinch-zoomed (browser tabs ignore user-scalable=no for
      // accessibility): height/offsetTop now describe the zoom pan, not a
      // keyboard pan — chasing them would shrink the shell and fight the
      // user's native panning. Release everything and let native zoom win;
      // the resize/scroll events fired by zooming back out re-arm us.
      // (Only scale > 1 disarms: a quirk reporting scale slightly BELOW 1
      // must not permanently disable the keyboard guard.)
      if ((vv.scale ?? 1) > 1.01) {
        release();
        return;
      }

      const main = document.getElementById("Main");
      // The snap-back is only safe where the #Main shell consumes
      // --app-height (ChatLayout/AuthLayout) — there the shrink in (1)
      // already keeps the focused field visible. Routes without the shell
      // (guest meet, standalone meet — plain h-dvh pages) never shrink,
      // so undoing WebKit's pan there would hide the focused field behind
      // the keyboard. Leave those routes fully native.
      //
      // Deliberately ahead of the focus gate below: the shell is never
      // user-scrollable, so a non-zero scrollY can ONLY be a leftover
      // keyboard pan — and one that outlived the blur is exactly the kind
      // of stuck offset this hook exists to undo. Gating it on focus would
      // strand it.
      if (main && window.scrollY > 0) {
        window.scrollTo(0, 0);
      }

      // No keyboard can be on screen without a keyboard-summoning focus,
      // so whatever the (possibly stale) metrics claim, nothing may stay
      // held. See THE INVARIANT above.
      if (!isKeyboardSummoningFocus()) {
        clearGlue(); // the re-anchor is a keyboard countermeasure; never leave one behind
        scheduleRelease();
        return;
      }
      cancelRelease();

      held = true;
      if (!watchdogTimer) {
        watchdogTimer = window.setInterval(sync, WATCHDOG_MS);
      }
      // Skip no-op writes: the watchdog re-runs this every tick while the
      // keyboard is up, and re-stamping the same value would keep the
      // shell's height transition perpetually re-armed.
      const height = `${Math.round(vv.height)}px`;
      if (root.style.getPropertyValue(APP_HEIGHT_VAR) !== height) {
        root.style.setProperty(APP_HEIGHT_VAR, height);
      }
      const offset = Math.max(0, Math.round(vv.offsetTop));
      const transform = offset ? `translateY(${offset}px)` : "";
      for (const el of [scrimEl, main]) {
        if (el && el.style.transform !== transform) {
          el.style.transform = transform;
        }
      }
    };

    const clearSettleTimers = () => {
      for (const timer of settleTimers) {
        window.clearTimeout(timer);
      }
      settleTimers = [];
    };

    const schedule = () => {
      if (!frame) {
        frame = requestAnimationFrame(sync);
      }
      // WebKit can report stale height/offsetTop inside the handler
      // itself; delayed re-reads settle on the real values.
      clearSettleTimers();
      settleTimers = SETTLE_DELAYS_MS.map((delay) =>
        window.setTimeout(sync, delay),
      );
    };

    /**
     * Coming back from the background is where the stuck shell was born
     * and where it is cheapest to undo: drop every write, then re-derive
     * from fresh metrics. If a keyboard really is still up, the settle
     * reads re-apply the shrink within a frame or two.
     */
    const resync = () => {
      if (document.visibilityState === "hidden") {
        return;
      }
      release();
      schedule();
    };

    sync();
    vv.addEventListener("resize", schedule);
    vv.addEventListener("scroll", schedule);
    window.addEventListener("focusin", schedule);
    window.addEventListener("focusout", schedule);
    // Recovery paths for the events WebKit does NOT reliably fire on the
    // visual viewport: app resume, back/forward restore, and rotation.
    document.addEventListener("visibilitychange", resync);
    window.addEventListener("pageshow", resync);
    window.addEventListener("orientationchange", resync);
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      clearSettleTimers();
      vv.removeEventListener("resize", schedule);
      vv.removeEventListener("scroll", schedule);
      window.removeEventListener("focusin", schedule);
      window.removeEventListener("focusout", schedule);
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("pageshow", resync);
      window.removeEventListener("orientationchange", resync);
      window.removeEventListener("resize", schedule);
      release();
    };
  }, [scrimRef]);
}
